/**
 * Custom element that renders a group of absorbency-option elements with a progress bar.
 *
 * Attributes:
 * - max-count: total required units (number)
 * - product-type: "pad" | "tampon" | string used for the label text
 * - disabled: disables child controls when present
 * - increment: numeric increment size (defaults to 1)
 * - product-id: product ID (string)
 * - show-button-disabled-state: disables individual increment/decrement buttons when their action is unavailable
 * - animate-progress: animates the progress bar when its value changes
 * - animate-option-increments: animates an incremented option count toward the total
 * - show-valid-checkmark: shows an animated checkmark when the selected count is valid
 * - animate-invalid-count: animates the count text when a selection action is invalid
 * - show-max-selection-toast: shows a message when customers try to increment after reaching the max
 *
 * Usage:
 * <absorbency-selector max-count="16" product-type="pad" increment="6" product-id="1234567890">
 *   <absorbency-option label="Light" color="#fce8f1" increment="6" sku="light" quantity="1" is-default="1"></absorbency-option>
 *   ...
 * </absorbency-selector>
 *
 * Events (bubble + composed) emitted on any change:
 * - absorbency:change { detail: { selections, currentCount, maxCount, selectionValid } }
 *   selections: [{ sku, label, increment, quantity, units }]
 * 
 * Example for how to listen for changes:
 * <script>
  document.addEventListener('DOMContentLoaded', () => {
    const selector = document.querySelector('absorbency-selector');
    if (!selector) return;
    selector.addEventListener('absorbency:change', (event) => {
      // Replace with theme-specific integration (cart form, hidden inputs, etc.)
      console.log('Absorbency updated', event.detail);
    });
  });
</script>
 */
class AbsorbencySelector extends HTMLElement {
  static get observedAttributes() {
    return ['max-count', 'product-type', 'disabled', 'product-id', 'increment', 'show-button-disabled-state', 'animate-progress', 'animate-option-increments', 'show-valid-checkmark', 'animate-invalid-count', 'show-max-selection-toast'];
  }

  constructor() {
    super();
    this._elements = {};
    this.increment = this.getAttribute('increment');
    this.productId = this.getAttribute('product-id');
    this.handleIncrement = this.handleIncrement.bind(this);
    this.handleDecrement = this.handleDecrement.bind(this);
    this.deliveryFrequencyDropdown = document.getElementById(`delivery-frequency-dropdown-${this.productId}`);
    this.getCurrentSelection = this.getCurrentSelection.bind(this);
    this._lastProgressValue = null;
    this._progressAnimationTimer = null;
    this._invalidCountAnimationFrame = null;
    this._invalidCountAnimationTimer = null;
    this._maxSelectionToastTimer = null;
    this._countAnimationElements = new Set();
  }

  connectedCallback() {
    this.render();
    this.refreshChildren();
    this.updateCounts();
    this.addEventListener('absorbency:increment', this.handleIncrement);
    this.addEventListener('absorbency:decrement', this.handleDecrement);
    if (this.deliveryFrequencyDropdown) {
      this.deliveryFrequencyDropdown.addEventListener('delivery-frequency:change', this.handleDeliveryFrequencyChange.bind(this));
    } else {
      console.warn('No delivery frequency dropdown found for product', this.productId);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('absorbency:increment', this.handleIncrement);
    this.removeEventListener('absorbency:decrement', this.handleDecrement);
    window.clearTimeout(this._progressAnimationTimer);
    window.clearTimeout(this._invalidCountAnimationTimer);
    window.clearTimeout(this._maxSelectionToastTimer);
    if (this._invalidCountAnimationFrame) {
      window.cancelAnimationFrame(this._invalidCountAnimationFrame);
    }
    this._countAnimationElements.forEach((element) => element.remove());
    this._countAnimationElements.clear();
  }

  attributeChangedCallback(name, _oldValue, _newValue) {
    if (!this.isConnected) return;
    if (name === 'disabled') {
      this.updateDisabledState();
    }
    if (name === 'max-count' || name === 'product-type' || name === 'show-button-disabled-state' || name === 'animate-progress' || name === 'animate-option-increments' || name === 'show-valid-checkmark') {
      this.updateCounts();
    }
    if (name === 'show-max-selection-toast' && !this.showMaxSelectionToast) {
      this.hideMaxSelectionToast();
    }
  }

  get maxCount() {
    const value = Number(this.getAttribute('max-count'));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  set maxCount(value) {
    this.setAttribute('max-count', value);
  }

  get productType() {
    return this.getAttribute('product-type') || '';
  }

  set productType(value) {
    this.setAttribute('product-type', value);
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get showButtonDisabledState() {
    return this.hasAttribute('show-button-disabled-state');
  }

  set showButtonDisabledState(value) {
    if (value) {
      this.setAttribute('show-button-disabled-state', '');
    } else {
      this.removeAttribute('show-button-disabled-state');
    }
  }

  get animateProgress() {
    return this.hasAttribute('animate-progress');
  }

  set animateProgress(value) {
    if (value) {
      this.setAttribute('animate-progress', '');
    } else {
      this.removeAttribute('animate-progress');
    }
  }

  get animateOptionIncrements() {
    return this.hasAttribute('animate-option-increments');
  }

  set animateOptionIncrements(value) {
    if (value) {
      this.setAttribute('animate-option-increments', '');
    } else {
      this.removeAttribute('animate-option-increments');
    }
  }

  get showValidCheckmark() {
    return this.hasAttribute('show-valid-checkmark');
  }

  set showValidCheckmark(value) {
    if (value) {
      this.setAttribute('show-valid-checkmark', '');
    } else {
      this.removeAttribute('show-valid-checkmark');
    }
  }

  get animateInvalidCount() {
    return this.hasAttribute('animate-invalid-count');
  }

  set animateInvalidCount(value) {
    if (value) {
      this.setAttribute('animate-invalid-count', '');
    } else {
      this.removeAttribute('animate-invalid-count');
    }
  }

  get showMaxSelectionToast() {
    return this.hasAttribute('show-max-selection-toast');
  }

  set showMaxSelectionToast(value) {
    if (value) {
      this.setAttribute('show-max-selection-toast', '');
    } else {
      this.removeAttribute('show-max-selection-toast');
    }
  }

  get maxSelectionToastMessage() {
    return this.getAttribute('max-selection-toast-message') || "You've reached the maximum selection.";
  }

  get maxSelectionToastDuration() {
    const value = Number.parseFloat(this.getAttribute('max-selection-toast-duration'));
    return Number.isFinite(value) && value > 0 ? value * 1000 : 5000;
  }

  refreshChildren() {
    this._options = Array.from(this.querySelectorAll('absorbency-option')).map((el) => ({
      el,
      sku: el.sku,
      increment: el.increment,
      label: el.label,
      quantity: el.quantity
    }));
    this.updateDisabledState();
  }

  /**
   * Handles the change event from the delivery frequency dropdown
   * Updates the max count and resets the selections
   * @param {Event} event - The delivery frequency change event
   */
  handleDeliveryFrequencyChange(event) {
    const { maxQuantityMultiplier } = event.detail || {};
    this.hideMaxSelectionToast();
    this.maxCount = this.increment * maxQuantityMultiplier;
    this.resetSelections();
    this.updateCounts();
    this.emitChange();
  }

  handleIncrement(event) {
    const { sku, increment } = event.detail || {};
    const option = this._options.find((opt) => opt.sku === sku);
    if (!option || this.disabled) return;

    const currentUnits = this.currentUnits();
    const nextUnits = currentUnits + increment;
    if (this.maxCount && nextUnits > this.maxCount) {
      this.animateInvalidCountAttempt();
      if (currentUnits === this.maxCount) {
        this.showMaxSelectionToastMessage();
      }
      return;
    }

    option.el.quantity = option.el.quantity + 1;
    option.quantity = option.el.quantity;
    this.updateCounts();
    if (this.animateOptionIncrements && !this.prefersReducedMotion()) {
      this.animateOptionCountToTarget(option.el);
    }
    this.emitChange();
  }

  handleDecrement(event) {
    const { sku } = event.detail || {};
    const option = this._options.find((opt) => opt.sku === sku);
    if (!option || this.disabled) return;
    if (option.el.quantity <= 0) {
      this.animateInvalidCountAttempt();
      return;
    }

    option.el.quantity = option.el.quantity - 1;
    option.quantity = option.el.quantity;
    this.updateCounts();
    this.emitChange();
  }

  currentUnits() {
    return this._options.reduce((sum, option) => sum + option.el.quantity * option.increment, 0);
  }

  /**
   * Resets the selections to the default with quantities 
   * increased evenly to the current max count (derived from delivery frequency)
   */
  resetSelections() {
    const numDefaults = this._options.filter((option) => option.el.isDefault).length;
    const countPerDefault = this.maxCount / numDefaults;
    const quantityPerDefault = countPerDefault / this.increment;
    
    this._options.forEach((option) => {
      if (option.el.isDefault) {
        option.el.quantity = quantityPerDefault;
      } else {
        option.el.quantity = 0;
      }
    });
  
    this.updateCounts();
  }

  updateCounts() {
    if (!this._elements.progress || !this._elements.countText) return;

    const current = this.currentUnits();
    const max = this.maxCount;
    const selectionValid = max ? current === max : true;
    const progressValue = max ? Math.min(current / max, 1) : 0;

    this._elements.countText.textContent = current;
    this._elements.totalText.textContent = max || 0;
    this._elements.itemLabel.textContent =
      this.productType && this.productType.toLowerCase() === 'pad'
        ? 'pads'
        : this.productType && this.productType.toLowerCase() === 'tampon'
        ? 'tampons'
        : 'items';

    const progressValueChanged = this._lastProgressValue !== null && this._lastProgressValue !== progressValue;

    this._elements.progress.value = progressValue;
    this._lastProgressValue = progressValue;
    if (this.animateProgress && progressValueChanged) {
      this.animateProgressUpdate();
    }

    this._elements.progress.classList.toggle('progress-error', !selectionValid);
    this._elements.progress.setAttribute(
      'aria-label',
      `${current} of the required ${max || current} have been selected`
    );

    this._elements.countText.classList.toggle('progress-error', !selectionValid);
    this._elements.validCheckmark?.classList.toggle('is-visible', this.showValidCheckmark && selectionValid && max > 0);
    if (!selectionValid) {
      this.hideMaxSelectionToast();
    }

    const firstIncrement = this._options[0]?.increment || 1;
    this._elements.helperText.textContent = `* Select absorbency customization in counts of ${firstIncrement}`;
    this.updateButtonDisabledState(current, max);
  }

  animateProgressUpdate() {
    if (!this._elements.progressContainer) return;

    this._elements.progressContainer.classList.remove('is-progress-animating');
    window.requestAnimationFrame(() => {
      this._elements.progressContainer.classList.add('is-progress-animating');
      window.clearTimeout(this._progressAnimationTimer);
      this._progressAnimationTimer = window.setTimeout(() => {
        this._elements.progressContainer?.classList.remove('is-progress-animating');
      }, 280);
    });
  }

  animateInvalidCountAttempt() {
    if (!this.animateInvalidCount || this.prefersReducedMotion()) return;

    const countText = this._elements.countTextContainer;
    if (!countText) return;

    countText.classList.remove('is-invalid-action-animating');
    window.clearTimeout(this._invalidCountAnimationTimer);

    if (this._invalidCountAnimationFrame) {
      window.cancelAnimationFrame(this._invalidCountAnimationFrame);
    }

    this._invalidCountAnimationFrame = window.requestAnimationFrame(() => {
      countText.classList.add('is-invalid-action-animating');
      this._invalidCountAnimationFrame = null;
      this._invalidCountAnimationTimer = window.setTimeout(() => {
        countText.classList.remove('is-invalid-action-animating');
      }, 360);
    });
  }

  showMaxSelectionToastMessage() {
    if (!this.showMaxSelectionToast) return;

    const toast = this._elements.toastError;
    if (!toast) return;

    window.clearTimeout(this._maxSelectionToastTimer);
    toast.textContent = this.maxSelectionToastMessage;
    toast.classList.remove('is-visible');
    toast.getBoundingClientRect();
    toast.classList.add('is-visible');
    this._maxSelectionToastTimer = window.setTimeout(() => {
      this.hideMaxSelectionToast();
    }, this.maxSelectionToastDuration);
  }

  hideMaxSelectionToast() {
    window.clearTimeout(this._maxSelectionToastTimer);
    this._maxSelectionToastTimer = null;
    this._elements.toastError?.classList.remove('is-visible');
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  animateOptionCountToTarget(option) {
    const target = this._elements.countText;
    if (!target) return;

    const source = option.querySelector('.lola-absorbency-option-input');
    if (!source) return;

    const value = source.textContent.trim();
    if (!value || value === '0') return;

    const targetRect = target.getBoundingClientRect();
    const targetX = targetRect.left + targetRect.width / 2;
    const targetY = targetRect.top + targetRect.height / 2;
    const sourceRect = source.getBoundingClientRect();
    const sourceX = sourceRect.left + sourceRect.width / 2;
    const sourceY = sourceRect.top + sourceRect.height / 2;

    if (!sourceRect.width || !sourceRect.height) return;

    const flyer = document.createElement('span');
    const sourceStyles = window.getComputedStyle(source);
    const deltaX = targetX - sourceX;
    const deltaY = targetY - sourceY;
    const duration = this.optionIncrementAnimationSpeed;

    flyer.className = 'lola-absorbency-count-flyer';
    flyer.textContent = value;
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.left = `${sourceX}px`;
    flyer.style.top = `${sourceY}px`;
    flyer.style.fontFamily = sourceStyles.fontFamily;
    flyer.style.fontSize = sourceStyles.fontSize;
    flyer.style.fontWeight = sourceStyles.fontWeight;
    flyer.style.color = sourceStyles.color;

    document.body.appendChild(flyer);
    this._countAnimationElements.add(flyer);

    const keyframes = [
      {
        opacity: 0,
        transform: 'translate(-50%, -50%) translate(0, 0) scale(0.9)'
      },
      {
        opacity: 1,
        transform: 'translate(-50%, -50%) translate(0, -30px) scale(1.35)',
        offset: 0.28
      },
      {
        opacity: 0.9,
        transform: `translate(-50%, -50%) translate(${deltaX * 0.68}px, ${deltaY - 18}px) scale(1.05)`,
        offset: 0.72
      },
      {
        opacity: 0,
        transform: `translate(-50%, -50%) translate(${deltaX}px, ${deltaY}px) scale(0.45)`
      }
    ];

    if (typeof flyer.animate !== 'function') {
      window.setTimeout(() => this.removeCountFlyer(flyer), duration + 60);
      return;
    }

    const animation = flyer.animate(keyframes, {
      duration,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards'
    });

    animation.finished
      .catch(() => {})
      .then(() => this.removeCountFlyer(flyer));
  }

  removeCountFlyer(flyer) {
    flyer.remove();
    this._countAnimationElements.delete(flyer);
  }

  get optionIncrementAnimationSpeed() {
    const value = Number.parseInt(
      window.getComputedStyle(this).getPropertyValue('--lola-absorbency-option-increment-animation-speed'),
      10
    );

    return Number.isFinite(value) && value > 0 ? value : 760;
  }

  updateButtonDisabledState(current = this.currentUnits(), max = this.maxCount) {
    if (!this._options) return;

    this._options.forEach((option) => {
      if (this.disabled) {
        option.el.disabled = true;
        return;
      }

      option.el.disabled = false;
      option.el.incrementDisabled = this.showButtonDisabledState && max && current + option.el.increment > max;
      option.el.decrementDisabled = this.showButtonDisabledState && option.el.quantity <= 0;
    });
  }

  getCurrentSelection() {
    const selections = this._options.map((option) => ({
      sku: option.el.sku,
      bundleVariantId: option.el.bundleVariantId,
      label: option.el.label,
      increment: option.el.increment,
      quantity: option.el.quantity,
      units: option.el.quantity * option.el.increment,
      isDefault: option.el.isDefault,
      padsMixedVariantId: option.el.padsMixedVariantId,
      selectionValid: this.maxCount ? this.currentUnits() === this.maxCount : true,
      currentCount: this.currentUnits(),
      maxCount: this.maxCount,
    }));

    return {
      selections,
      currentCount: this.currentUnits(),
      maxCount: this.maxCount,
      selectionValid: this.maxCount ? this.currentUnits() === this.maxCount : true
    }
  }

  emitChange() {
    const detail = this.getCurrentSelection();

    this.dispatchEvent(
      new CustomEvent('absorbency:change', {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }

  updateDisabledState() {
    const disabled = this.disabled;
    this._options.forEach((option) => {
      option.el.disabled = disabled;
    });
    if (!disabled) {
      this.updateButtonDisabledState();
    }
  }

  render() {
    const existingOptions = Array.from(this.querySelectorAll('absorbency-option'));

    this.innerHTML = `
      <div class="lola-absorbency-selector">
        <fieldset style="border: none; padding: 0; margin: 0;">
          <legend class="absorbency-selector-legend visually-hidden">Customize Your Box:</legend>

          <div class="lola-absorbency-progress-container">
            <div class="lola-absorbency-count-text">
              <span class="lola-absorbency-count-current"></span>/<span class="lola-absorbency-count-max"></span>
              <span class="lola-absorbency-item-label"></span>
              <span class="lola-absorbency-count-checkmark" aria-hidden="true">
                <svg class="lola-absorbency-count-checkmark-icon" viewBox="0 0 20 20" focusable="false">
                  <path d="M4 10.5 8.2 14.5 16 5.5"></path>
                </svg>
              </span>
              <div class="lola-toast-error-container" role="alert"></div>
            </div>
            <progress value="0" class=""></progress>
          </div>

          <div class="lola-absorbency-quantity-container"></div>
        </fieldset>
        <div class="lola-absorbency-helper-text"></div>
      </div>
    `;

    this._elements = {
      progress: this.querySelector('progress'),
      progressContainer: this.querySelector('.lola-absorbency-progress-container'),
      countTextContainer: this.querySelector('.lola-absorbency-count-text'),
      countText: this.querySelector('.lola-absorbency-count-current'),
      totalText: this.querySelector('.lola-absorbency-count-max'),
      itemLabel: this.querySelector('.lola-absorbency-item-label'),
      validCheckmark: this.querySelector('.lola-absorbency-count-checkmark'),
      toastError: this.querySelector('.lola-toast-error-container'),
      helperText: this.querySelector('.lola-absorbency-helper-text'),
      quantitySlot: this.querySelector('.lola-absorbency-quantity-container')
    };

    // Move existing options into the quantity container to avoid re-rendering them.
    existingOptions.forEach((option) => {
      this._elements.quantitySlot.appendChild(option);
    });
  }
}

if (!customElements.get('absorbency-selector')) {
  customElements.define('absorbency-selector', AbsorbencySelector);
}
